/**
 * Product analytics for People Space, via Segment.
 *
 * Purpose: see how the space is actually used and where people get stuck — page flows, feature
 * adoption, and the errors users hit.
 *
 * Segment is FINN's tracking front door (tooling-team architecture): the app sends events to ONE
 * Segment source, and Segment fans them out to the data warehouse and to Amplitude. So the only
 * credential in this repo is the Segment WRITE KEY; the Amplitude project key lives in Segment's
 * destination config, and new sinks are a Segment console change, not a code change.
 *
 * Configuration is one env var, `VITE_SEGMENT_WRITE_KEY` (a browser write key is a public
 * identifier, like the Firebase config — but env-driven so local dev sends nothing by default).
 * Without it every function here is a silent no-op: analytics must never be the reason a page
 * breaks or a test suite phones home.
 *
 * ── PII rule ─────────────────────────────────────────────────────────────────────────────────
 * This app shows candidates' data. NONE of it may reach analytics: no candidate names, emails,
 * subjects or message text — in event properties or anywhere else. Track categories, counts,
 * booleans and durations instead ("draft_saved {category: 'reschedule'}", never the candidate).
 * The signed-in RECRUITER's email is the analytics user id — that is FINN-internal and is the
 * dimension analyses group by.
 */
import { AnalyticsBrowser } from '@segment/analytics-next';
import { isBlockedHost as isLocalHost } from './hostGuard';
import { currentPath, redactPath } from './redactPath';

const WRITE_KEY = import.meta.env.VITE_SEGMENT_WRITE_KEY || '';

/**
 * Kill switch: no analytics from local dev, even when a write key is set — the guard applies HERE,
 * not only in logAnalyticsEvent, so direct callers (trackPage, identifyUser, feature events)
 * cannot bypass it. `VITE_SEGMENT_DEBUG=true` opts a local run back in, for verifying events in
 * Segment's debugger during setup.
 */
function isBlockedHost(): boolean {
  return isLocalHost(import.meta.env.VITE_SEGMENT_DEBUG);
}

let client: AnalyticsBrowser | null = null;

/** True when a write key is configured and this host may send — callers can skip work on false. */
export const analyticsEnabled = () => Boolean(WRITE_KEY) && !isBlockedHost();

/**
 * Idempotent; safe to call from anywhere that might run first. AnalyticsBrowser.load buffers
 * calls until the SDK is ready, so callers never await it. Never throws: analytics being broken
 * must not break the page (a failed load returns null and the app carries on).
 */
export function initAnalytics(): AnalyticsBrowser | null {
  if (!analyticsEnabled()) return null;
  if (!client) {
    try {
      client = AnalyticsBrowser.load({ writeKey: WRITE_KEY });
    } catch {
      return null;
    }
  }
  return client;
}

/** Consume a tracking promise: a failed analytics call is logged nowhere and breaks nothing. */
const swallow = (p: Promise<unknown> | undefined) => void p?.catch(() => {});

/**
 * Tie events to the signed-in recruiter (FINN-internal identity, not candidate data).
 * Pass null on sign-out so a shared browser does not keep attributing to the previous user.
 */
export function identifyUser(email: string | null): void {
  const analytics = initAnalytics();
  if (!analytics) return;
  const id = email?.trim().toLowerCase();
  if (id) swallow(analytics.identify(id));
  else swallow(analytics.reset() as unknown as Promise<unknown>);
}

export type EventProps = Record<string, string | number | boolean | null | undefined>;

/** Fire-and-forget event. Callers must respect the PII rule in the module header. */
export function trackEvent(eventName: string, props: EventProps = {}): void {
  const analytics = initAnalytics();
  if (!analytics) return;
  swallow(analytics.track(eventName, props));
}

/**
 * SPA route change — Segment's page() feeds "which views get used" in every downstream tool.
 * Redacted defensively: the caller already skips `/book/*`, but this module must not depend on
 * every future caller remembering to.
 */
export function trackPage(path: string): void {
  const analytics = initAnalytics();
  if (!analytics) return;
  const safe = redactPath(path);
  swallow(analytics.page(undefined, safe, { path: safe }));
}

/**
 * Client-side failures are exactly the "where do people have issues" signal, so they get a
 * uniform event — but STRUCTURED METADATA ONLY, never the message text. An error string can
 * quote anything that was on screen or in an API response (a candidate's name, a subject line),
 * and no redaction list can promise to catch all of it, so the text does not travel at all.
 * `source` + `kind` + `path` still group failures well; the full message stays in the console
 * for whoever debugs it.
 *
 * The path is REDACTED (`currentPath`), not raw: this net is installed globally, so it also fires
 * on the public `/book/:token` pages where the pathname is the candidate's booking credential.
 *
 * Takes an already-categorised `kind` rather than the thrown value: `error.name` is writable and
 * can hold any string, so the classification happens once in `nativeAnalytics.errorKind` and this
 * function never sees the raw error at all.
 */
export function trackClientError(source: string, kind: string): void {
  if (!analyticsEnabled()) return;
  trackEvent('client_error', { source, kind, path: currentPath() });
}

/**
 * The global net that turns uncaught errors into `client_error` lives in `nativeAnalytics.ts`,
 * not here: it feeds every sink, so it must not be gated on Segment's write key.
 */

/** @internal test seam */
export function _resetAnalyticsForTests(): void {
  client = null;
}
